import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import ServiceService, {
  IUpdateServiceParams,
} from '@clickersphere/core/services/ServiceService';
import useToast from '../useToast';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { ServiceQueryData, SERVICES_QUERY_KEY } from './useServices';

export function useUpdateService() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();
  const { toast } = useToast();
  const { t } = useTranslation();

  const { isPending: updateServiceIsPending, mutateAsync: updateServiceAsync } =
    useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: IUpdateServiceParams;
      }) => ServiceService.update(id, data),
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(SERVICES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([SERVICES_QUERY_KEY, variables.id]),
        });

        const oldServices = queryClient.getQueryData<ServiceQueryData[]>(
          buildQueryKey(SERVICES_QUERY_KEY),
        );
        const oldService = queryClient.getQueryData<ServiceQueryData>(
          buildQueryKey([SERVICES_QUERY_KEY, variables.id]),
        );

        const dataWithoutImage: Omit<IUpdateServiceParams, 'imageUrl'> = {
          name: variables.data.name,
          description: variables.data.description,
          price: variables.data.price,
        };

        queryClient.setQueryData<ServiceQueryData[]>(
          buildQueryKey(SERVICES_QUERY_KEY),
          (old) =>
            old?.map((item) =>
              item.id === variables.id
                ? { ...item, ...dataWithoutImage, queryStatus: 'pending' }
                : item,
            ),
        );

        queryClient.setQueryData<ServiceQueryData>(
          buildQueryKey([SERVICES_QUERY_KEY, variables.id]),
          (old) =>
            old?.id === variables.id
              ? { ...old, ...dataWithoutImage, queryStatus: 'pending' }
              : old,
        );

        return { oldServices, oldService };
      },
      onSuccess: async (_data, variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(SERVICES_QUERY_KEY),
        });

        queryClient.setQueryData<ServiceQueryData[]>(
          buildQueryKey(SERVICES_QUERY_KEY),
          (old) =>
            old?.map((item) =>
              item.id === variables.id
                ? { ...item, queryStatus: undefined }
                : item,
            ),
        );

        queryClient.setQueryData<ServiceQueryData>(
          buildQueryKey([SERVICES_QUERY_KEY, variables.id]),
          (old) =>
            old?.id === variables.id ? { ...old, queryStatus: undefined } : old,
        );

        if (variables.data.imageUrl !== undefined) {
          await queryClient.invalidateQueries({
            queryKey: buildQueryKey(SERVICES_QUERY_KEY),
          });
        }

        toast(t('catalog.toasts.serviceUpdated'), 'success');
      },
      onError: async (_error, variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(SERVICES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([SERVICES_QUERY_KEY, variables.id]),
        });

        queryClient.setQueryData(
          buildQueryKey(SERVICES_QUERY_KEY),
          context?.oldServices,
        );
        queryClient.setQueryData(
          buildQueryKey([SERVICES_QUERY_KEY, variables.id]),
          context?.oldService,
        );

        toast(t('catalog.toasts.serviceUpdateError'), 'error');
      },
    });

  return {
    updateServiceAsync,
    updateServiceIsPending,
  };
}
